// Breaks a login redirect loop (SPNS-506).
//
// Loop: login works, but an ordinary API call then returns 401. The 401
// interceptor logs out and sends the user to /login, /login sends them to LUC,
// LUC (still signed in) sends them straight back, login works, the call 401s
// again, and so on, with nothing on screen.
//
// The interceptor records every session-expired redirect here. A second one
// within LOOP_WINDOW_MS means we came straight back from LUC and failed again,
// so we leave a one-shot block that /login reads instead of redirecting to LUC.
// /login then shows the error screen, and its "Try again" button is a normal,
// user-initiated redirect.
//
// State lives in sessionStorage so it survives the round trip through LUC.
// Every access is guarded: if storage is unavailable the guard does nothing and
// behavior is the same as before this file existed.
const REDIRECT_AT_KEY = 'login_redirect_at';
const BLOCK_KEY = 'login_redirect_blocked';
export const LOOP_WINDOW_MS = 30000;

// Several calls can fail with 401 at once. Only the first one in a page load
// counts: the loop always spans a full page load (the trip through LUC).
let recordedThisPage = false;

const read = (key) => {
  try {
    return window.sessionStorage.getItem(key);
  } catch (e) {
    return null;
  }
};

const write = (key, value) => {
  try {
    window.sessionStorage.setItem(key, value);
  } catch (e) {
    // storage unavailable: no loop protection, nothing else to do
  }
};

const remove = (key) => {
  try {
    window.sessionStorage.removeItem(key);
  } catch (e) {
    // see write()
  }
};

// Call once per session-expired logout, before /login redirects to LUC.
export const recordSessionExpiredRedirect = (now = Date.now()) => {
  if (recordedThisPage) return;
  recordedThisPage = true;

  const previous = Number(read(REDIRECT_AT_KEY));
  if (previous && now - previous >= 0 && now - previous < LOOP_WINDOW_MS) {
    write(BLOCK_KEY, String(now));
  }
  write(REDIRECT_AT_KEY, String(now));
};

// Call from /login. True when it must not redirect to LUC this time. Consumes
// the block, so a later visit to /login (or "Try again") redirects normally.
export const consumeLoginRedirectBlock = () => {
  const blocked = read(BLOCK_KEY) !== null;
  if (blocked) {
    // REDIRECT_AT_KEY stays, so "Try again" followed by another 401 within the
    // window is caught at once instead of looping once more.
    remove(BLOCK_KEY);
  }
  return blocked;
};

// Test helper: each test starts a fresh "page load".
export const resetLoginLoopGuardForTests = () => {
  recordedThisPage = false;
};
